import ProgramUtils from 'core-system/Program/ProgramUtils'
import React, { useState } from 'react'
import ProgramEditSubSection from 'features/Programs/Editing/Components/ProgramEditSubSection'
import Divider from 'core-system/Divider'
import Text from 'core-system/Text'
import FlexContainer from 'core-system/FlexContainer'
import SvgTooltip from 'core-system/Tooltip/SvgTooltip'
import InfoIcon from 'core-system/Icons/Misc/Info'
import palette from 'core-system/Themes/palette'
import HorizontalScroller from 'core-system/HorizontalScroller'
import HorizontalScrollerUtils from 'core-system/HorizontalScroller/HorizontalScrollerUtils'
import RandomUtils from 'shared/RandomUtils'
import styled from 'styled-components'
import { useSelector } from 'react-redux'
import { AppState } from 'redux/config/store'
import ServiceProviderSelectorModal from 'core-system/ServiceProvider/ServiceProviderSelectorModal'
import SegmentService from 'redux/config/services/SegmentService'
import { GovernmentGrhProgram } from 'features/Programs/Shared/types'
import ScrollerElement from 'core-system/HorizontalScroller/ScrollerElement'
import AlertBox from 'core-system/AlertBanner/AlertBox'
import wordings from 'features/Programs/Shared/wordings.json'
import { Merchant } from 'redux/programs/programsTypes'

const HeaderContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const HyperLink = styled(Text)`
  cursor: pointer;
  color: ${(props) => props.theme.palette.secondary.purple700};

  &:hover {
    color: ${(props) => props.theme.palette.text.primary};
  }
`

const PillContainer = styled.div`
  padding: 0.25rem 0.3125rem 0.25rem 0.25rem;
  background-color: ${(props) => props.theme.palette.secondary.purple600};
  border-radius: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: 0.5rem;
  min-width: 1.5rem;
`

interface ProgramOptionProps extends React.HtmlHTMLAttributes<HTMLDivElement> {
  active: boolean
  type: string
  handleToggle: (type: string) => void
  canEdit?: boolean
  children?: React.ReactNode
  availableMerchantIds?: string[]
  activeMerchantIds?: string[]
  readOnly?: boolean
  handleProviderChange?: (newActiveProviders: string[]) => void
  governmentGrhPrograms?: GovernmentGrhProgram[]
  /** When set, used instead of redux merchantsMap (e.g. transit agencies). */
  providersMap?: Dictionary<Merchant>
  selectorModalTitle?: string
  singleSelect?: boolean
}

const ProgramOption = React.memo((props: ProgramOptionProps) => {
  const {
    active,
    type,
    handleToggle,
    canEdit = true,
    children,
    availableMerchantIds,
    activeMerchantIds,
    readOnly = false,
    handleProviderChange,
    governmentGrhPrograms,
    providersMap: providersMapProp,
    selectorModalTitle = 'Available Service Providers',
    singleSelect = false,
  } = props

  const [isModalOpen, setIsModalOpen] = useState(false)

  const reduxMerchantsMap = useSelector(
    (state: AppState) => state.programs.merchantsMap
  )
  const merchantsMap = providersMapProp || reduxMerchantsMap

  const hasAvailableProviders =
    availableMerchantIds?.length > 0 &&
    (!readOnly || activeMerchantIds?.length > 0)

  const MerchantList = () => (
    <>
      <Divider marginY='1.5rem' />
      <HeaderContainer>
        {readOnly ? (
          <FlexContainer>
            <Text variant='action4' marginRight='0.375rem'>
              {ProgramUtils.optionsCopy[type].infoTitle ||
                `Service Provider${RandomUtils.pluralCheck(
                  activeMerchantIds.length
                )}`}
            </Text>
            {ProgramUtils.optionsCopy[type].infoDescription && (
              <SvgTooltip
                svg={
                  <InfoIcon
                    width={20}
                    height={20}
                    color={palette.text.secondary}
                  />
                }
                height='1.25rem'
                isMultiLine={true}
                description={
                  'Commuters will be able to spend their budget on all local public transportation providers beyond only this list.'
                }
              />
            )}
          </FlexContainer>
        ) : (
          <FlexContainer>
            <Text variant='action4'>
              Service Provider
              {RandomUtils.pluralCheck(activeMerchantIds.length)}
            </Text>
            <PillContainer>
              <Text variant='action4' color='white'>
                {activeMerchantIds.length}
              </Text>
            </PillContainer>
          </FlexContainer>
        )}
        {hasAvailableProviders && !readOnly && (
          <HyperLink
            variant='hyperlink'
            onClick={() => setIsModalOpen(true)}
            data-cy={`${type}-providers`}
          >
            {canEdit ? 'Edit' : 'View All'}
          </HyperLink>
        )}
      </HeaderContainer>
      {hasAvailableProviders ? (
        <HorizontalScroller
          translationPerClick={160}
          marginTop='1rem'
          resetOnChildrenUpdate
        >
          {HorizontalScrollerUtils.getProviders(
            activeMerchantIds,
            merchantsMap
          )}
        </HorizontalScroller>
      ) : (
        <Text variant='action3' marginTop='1rem'>
          Sorry, there are currently no {ProgramUtils.optionsCopy[type].title}{' '}
          providers in your area!
        </Text>
      )}
    </>
  )

  const GovernmentGrhPrograms = () => (
    <>
      <AlertBox
        title='Program Acknowledgement'
        variant='purple'
        message={wordings.governmentGrhProgramsAcknowledgement}
      />
      <Divider marginY='1.5rem' />
      <HeaderContainer>
        <Text variant='action4'>Programs Available</Text>
      </HeaderContainer>
      {governmentGrhPrograms.length > 0 ? (
        <HorizontalScroller
          translationPerClick={160}
          marginTop='1rem'
          resetOnChildrenUpdate
        >
          {governmentGrhPrograms.map((program) => (
            <ScrollerElement
              logoElement={
                <img
                  src={program.iconUrl}
                  style={{ maxHeight: 32 }}
                  alt={program.name}
                />
              }
              name={program.name}
              onClick={() => window.open(program.websiteUrl, '_blank')}
            />
          ))}
        </HorizontalScroller>
      ) : (
        <Text variant='action3' marginTop='1rem'>
          Sorry, there are currently no programs available!
        </Text>
      )}
    </>
  )

  return (
    <ProgramEditSubSection
      title={ProgramUtils.optionsCopy[type].title}
      description={ProgramUtils.optionsCopy[type].description}
      canEdit={canEdit}
      active={active}
      handleToggle={handleToggle}
      type={type}
    >
      {active && (
        <>
          <div>{children}</div>
          {availableMerchantIds && <MerchantList />}
          {governmentGrhPrograms && <GovernmentGrhPrograms />}
        </>
      )}
      {activeMerchantIds && (
        <ServiceProviderSelectorModal
          open={isModalOpen}
          title={selectorModalTitle}
          closeModal={() => {
            setIsModalOpen(false)
            SegmentService.track('providers-modal-toggle', {
              action: 'close',
              state: 'edit',
              type,
            })
          }}
          activeProviders={activeMerchantIds}
          availableProviders={availableMerchantIds}
          onSave={(newActiveProviders: string[]) =>
            handleProviderChange(newActiveProviders)
          }
          modeTypeFilters={!providersMapProp}
          providersMap={providersMapProp}
          singleSelect={singleSelect}
        />
      )}
    </ProgramEditSubSection>
  )
})

// Helps to identify component in React error logs
if (process.env.NODE_ENV !== 'production') {
  ProgramOption.displayName = 'ProgramOption'
}

export default ProgramOption
